import axios from "axios";
import { ErrorMessage, Field } from "formik";
import React, { useCallback, useContext, useState } from "react";
import _ from "lodash";
import { AppContext } from "../../state/index";
import { renderBaseUrlForApi } from "../../utils/index";

const Input: ({
  label,
  required,
  backgroundColor,
  name,
  type,
  placeHolder,
  customHeight,
  isMobile,
  labelClassNames,
  labelStyle,
  mapField,
  mapName,
  customFontSize,
  customStyle,
  disable,
  dominCheck,
  countryCodeName,
}: {
  label?: string;
  required?: boolean;
  backgroundColor?: string;
  name: string;
  mapName?: string;
  type?: string;
  placeHolder?: string;
  customHeight?: string;
  labelClassNames?: string;
  labelStyle?: React.CSSProperties;
  mapField?: boolean;
  customFontSize?: string;
  customStyle?: any;
  disable?: boolean;
  isMobile?: boolean;
  dominCheck?: boolean;
  countryCodeName?: string;
  wantButton?: string;
}) => React.JSX.Element = ({
  label,
  required,
  backgroundColor,
  name,
  type,
  placeHolder,
  mapField,
  mapName,
  labelClassNames,
  labelStyle,
  customHeight,
  customFontSize,
  customStyle,
  disable,
  isMobile,
  dominCheck,
  countryCodeName,
  wantButton,
}) => {
    const context = useContext(AppContext);
    const { globalData = { countries: [] } } = context || {};

    const [domainIsExist, setDomainIsExist] = useState("");

    const updateLocationFromAddress = async (enteredAddress, name, form) => {
      try {
        const response = await axios.get(
          `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(
            enteredAddress
          )}&key=${process.env.REACT_APP_GOOGLE_API_KEY!}`,
          {
            headers: {
              "Content-Type": "application/json",
            },
          }
        );

        if (response?.data?.results?.length > 0) {
          const location = response?.data?.results?.[0]?.geometry?.location;
          const formattedAddress =
            response?.data?.results?.[0]?.formatted_address;
          let place_id = response?.data?.results?.[0]?.place_id;
          let city = "";
          let postalCode = "";
          let country = "";
          let state = "";
          response?.data?.results?.[0]?.address_components.forEach(
            (component) => {
              if (component.types.includes("postal_code")) {
                postalCode = component.long_name;
              }
              if (component.types.includes("country")) {
                country = component.long_name;
              }
              if (component.types.includes("administrative_area_level_3")) {
                city = component.long_name;
              }
              if (component.types.includes("administrative_area_level_1")) {
                state = component.long_name;
              }
            }
          );
          form.setFieldValue(mapName, {
            lat: location.lat,
            lng: location.lng,
            address: formattedAddress,
            city,
            state,
            country,
            postalCode,
            place_id,
          });
        }
      } catch (error) {
        // Handle error if needed
      }
    };

    const debouncedUpdateLocation = useCallback(
      _.debounce((item, mapName, form) => {
        updateLocationFromAddress(item, mapName, form);
      }, 500),
      []
    );

    const handleChange = (event, form) => {
      const item = event.target.value;
      if (mapField && !!item) {
        debouncedUpdateLocation(item, mapName, form);
      }
      form.setFieldValue(name, item);
    };

    return (
      <div className="flex flex-col gap-1 w-full">
        <div className="flex items-center justify-between">
          {!!label && (
            <label
              className={`font-manropeSB text-[#4a5568] text-[1.8vh] ${labelClassNames || ""}`}
            >
              {label} {!!required && <span className="text-red-700">*</span>}
            </label>)}
          <span>
            {isMobile && (
              <ErrorMessage
                name={`${name}_country`}
                component="div"
                className="text-red-600 text-[2.2vh]"
              />
            )}
            <ErrorMessage
              name={name}
              component="div"
              className="text-red-600 text-[2.2vh]"
            />
          </span>
        </div>
        <Field name={name}>
          {({ field, form, meta }) => (
            <div
              className="flex items-center gap-2 w-full border rounded-xl py-[0.4vh] px-2"
              style={{
                backgroundColor: backgroundColor || "white",
                borderColor: meta.touched && meta.error ? "red" : "#CECECE",
              }}
            >
              {isMobile && (
                <Field name={countryCodeName || "COUNTRY_CODE"}>
                  {({ field, form, meta }) => (
                    <div className="relative flex-shrink-0 w-[10vh]">
                      <select
                        {...field}
                        className={`w-full border border-[#CECECE] rounded-md px-1 py-[0.6vh] text-[2vh] appearance-none bg-white focus:outline-none focus:ring-0 ${meta.touched && meta.error
                          ? "border-red-600"
                          : "#CECECE"
                          }`}
                      >
                        <option value="">Select</option>
                        {globalData?.countries?.map((country) => (
                          <option
                            key={country.COUNTRY_CODE}
                            value={country.COUNTRY_CODE}
                          >
                            {country.COUNTRY_CODE}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </Field>
              )}
              <div className="flex justify-between items-center gap-2 w-full">
                {Boolean(dominCheck && !!domainIsExist) ? (
                  <img
                    src={
                      domainIsExist === "YES"
                        ? "/images/RedCross.svg"
                        : "/images/GreenTick.svg"
                    }
                    className="w-6 h-6"
                    alt="Status"
                  />
                ) : null}
                <input
                  disabled={disable}
                  className="rounded-lg w-full px-2 text-[2.2vh] md:text-sm placeholder-[#787878] focus:outline-none focus:ring-0 focus:border-transparent"
                  placeholder={placeHolder || ""}
                  style={{
                    backgroundColor: backgroundColor || "white",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    // fontSize: customFontSize || "0.75rem",
                    fontSize: "1.8vh",
                    height: "5.9vh",
                    ...(customStyle || {}),
                  }}
                  type={type || "text"}
                  required={required}
                  value={field?.value?.address || field.value}
                  onChange={(item) => {
                    let val = item.target.value;
                    if (type === "number") {
                      val = val.replace(/[^0-9]/g, ""); // Allow only positive numbers
                    }
                    handleChange({ target: { value: isMobile ? val.slice(0, 10) : val } }, form);
                  }}
                  onWheel={(e) => e.currentTarget.blur()}
                  onKeyDown={(e) => {
                    if (
                      type === "number" &&
                      (e.key === "ArrowUp" || e.key === "ArrowDown")
                    ) {
                      e.preventDefault();
                    }
                  }}
                  onBlur={async () => {
                    if (dominCheck) {
                      try {
                        const response = await axios.get(
                          `${renderBaseUrlForApi()}/master/institute/checkDomainDuplicate?domain_name=${field?.value}`
                        );
                        if (
                          !(response?.data?.message === "Domain name is available")
                        ) {
                          setDomainIsExist("YES");
                          form.setFieldError(
                            name,
                            "Domain name is not available"
                          );
                        } else {
                          setDomainIsExist("NO");
                          form.setFieldError(name, "");
                        }
                      } catch (err) {
                        setDomainIsExist("");
                      }
                    }
                  }}
                />
                {!!wantButton && (
                  <button
                    type="button"
                    disabled={true}
                    className="text-[2.2vh] px-3 py-1 bg-gray-200 rounded-lg border border-gray-300"
                  >
                    {wantButton}
                  </button>
                )}
              </div>
            </div>
          )}
        </Field>
      </div>
    );
  };

export default Input;